import { baseApi, PaginatedResponse } from 'sama-shared';
import type {
  CreateDeliveryProviderPayoutAccountAdminDto,
  DeliveryProviderPayoutAccount,
  GetDeliveryPayoutAccountFilterAdminDto,
  UpdateDeliveryProviderPayoutAccountAdminDto,
} from './types/payout-account';

export const deliveryPayoutAccountApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['DeliveryPayoutAccount'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getDeliveryPayoutAccounts: builder.query<
        PaginatedResponse<DeliveryProviderPayoutAccount>,
        GetDeliveryPayoutAccountFilterAdminDto | void
      >({
        query: (params) => ({
          url: '/admin/delivery-payout-account',
          method: 'GET',
          params: params || {},
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({ type: 'DeliveryPayoutAccount' as const, id })),
                { type: 'DeliveryPayoutAccount', id: 'LIST' },
              ]
            : [{ type: 'DeliveryPayoutAccount', id: 'LIST' }],
      }),

      getDeliveryPayoutAccountById: builder.query<DeliveryProviderPayoutAccount, string>({
        query: (id) => ({
          url: `/admin/delivery-payout-account/${id}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, id) => [{ type: 'DeliveryPayoutAccount', id }],
      }),

      createDeliveryPayoutAccount: builder.mutation<
        DeliveryProviderPayoutAccount,
        CreateDeliveryProviderPayoutAccountAdminDto
      >({
        query: (newAccount) => ({
          url: '/admin/delivery-payout-account',
          method: 'POST',
          body: newAccount,
        }),
        invalidatesTags: [{ type: 'DeliveryPayoutAccount', id: 'LIST' }],
      }),

      updateDeliveryPayoutAccount: builder.mutation<
        DeliveryProviderPayoutAccount,
        { id: string; body: UpdateDeliveryProviderPayoutAccountAdminDto }
      >({
        query: ({ id, body }) => ({
          url: `/admin/delivery-payout-account/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'DeliveryPayoutAccount', id },
          { type: 'DeliveryPayoutAccount', id: 'LIST' },
        ],
      }),

      deleteDeliveryPayoutAccount: builder.mutation<void, string>({
        query: (id) => ({
          url: `/admin/delivery-payout-account/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, id) => [
          { type: 'DeliveryPayoutAccount', id },
          { type: 'DeliveryPayoutAccount', id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetDeliveryPayoutAccountsQuery,
  useGetDeliveryPayoutAccountByIdQuery,
  useCreateDeliveryPayoutAccountMutation,
  useUpdateDeliveryPayoutAccountMutation,
  useDeleteDeliveryPayoutAccountMutation,
} = deliveryPayoutAccountApi;
